import { Bbox } from 'js/helpers/MapHelper';
import { DRAWING_MODE } from 'js/constants/map';

/**
 * Helper function to convert Bbox to LatLng points
 * @param {google.maps.LatLngBounds} bounds - The bounds to convert
 * @returns {Array<{lat: number, lng: number}>} Array of coordinate objects
 */
export const bboxToPoints = (bounds) => Bbox.toArray(bounds).map(({ lat, lon }) => ({ lat, lng: lon }));

/**
 * Helper function to check if any points are contained in bounds
 * @param {Array<google.maps.LatLng>} points - Array of points to check
 * @param {google.maps.LatLngBounds} bounds - Bounds to check against
 * @returns {boolean} True if any point is contained in bounds
 */
export const hasPointsInBounds = (points, bounds) => points.some(point => bounds.contains(point));

/**
 * Converts a circle to an array of points for intersection calculations
 * @param {google.maps.Circle} circle - The circle shape
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {Array<google.maps.LatLng>} Array of points representing the circle
 */
export const parseCircleToPoints = (circle, spherical) => {
    const points = [];
    const numberOfPoints = 36; // the more points, the more accurate

    for (let i = 0; i < numberOfPoints; i++) {
        const angle = (2 * Math.PI * i) / numberOfPoints;
        const pointAtAnAngle = spherical.computeOffset(
            circle.getCenter(),
            circle.getRadius(),
            angle * 180 / Math.PI
        );
        points.push(pointAtAnAngle);
    }

    return points;
};

/**
 * Checks if a line segment intersects with a rectangle
 * @param {google.maps.LatLng} segmentStart - Start point of line segment
 * @param {google.maps.LatLng} segmentEnd - End point of line segment
 * @param {google.maps.LatLngBounds} rectBounds - Rectangle bounds
 * @returns {boolean} True if segment intersects rectangle
 */
const doesLineSegmentIntersectRectangle = (segmentStart, segmentEnd, rectBounds) => {
    // Check if either endpoint is inside rectangle
    if (rectBounds.contains(segmentStart) || rectBounds.contains(segmentEnd)) {
        return true;
    }

    // Get rectangle corners
    const ne = rectBounds.getNorthEast();
    const sw = rectBounds.getSouthWest();
    const nw = new window.google.maps.LatLng(ne.lat(), sw.lng());
    const se = new window.google.maps.LatLng(sw.lat(), ne.lng());

    // Check intersection with all 4 rectangle edges
    return (
        doLineSegmentsIntersect(segmentStart, segmentEnd, nw, ne) || // top edge
        doLineSegmentsIntersect(segmentStart, segmentEnd, ne, se) || // right edge
        doLineSegmentsIntersect(segmentStart, segmentEnd, se, sw) || // bottom edge
        doLineSegmentsIntersect(segmentStart, segmentEnd, sw, nw)    // left edge
    );
};

/**
 * Checks if two line segments intersect using cross product method
 * @param {google.maps.LatLng} p1 - Start of first segment
 * @param {google.maps.LatLng} p2 - End of first segment
 * @param {google.maps.LatLng} p3 - Start of second segment
 * @param {google.maps.LatLng} p4 - End of second segment
 * @returns {boolean} True if segments intersect
 */
const doLineSegmentsIntersect = (p1, p2, p3, p4) => {
    const ccw = (a, b, c) => {
        return (c.lng() - a.lng()) * (b.lat() - a.lat()) >
               (b.lng() - a.lng()) * (c.lat() - a.lat());
    };

    return ccw(p1, p3, p4) !== ccw(p2, p3, p4) &&
           ccw(p1, p2, p3) !== ccw(p1, p2, p4);
};

/**
 * Checks if a line segment intersects with any edge of a polygon
 * @param {google.maps.LatLng} segmentStart - Start point of line segment
 * @param {google.maps.LatLng} segmentEnd - End point of line segment
 * @param {Array<google.maps.LatLng>} polygonPoints - Polygon vertices
 * @returns {boolean} True if segment intersects any polygon edge
 */
const doesLineSegmentIntersectPolygon = (segmentStart, segmentEnd, polygonPoints) => {
    for (let i = 0; i < polygonPoints.length; i++) {
        const edgeStart = polygonPoints[i];
        const edgeEnd = polygonPoints[(i + 1) % polygonPoints.length];

        if (doLineSegmentsIntersect(segmentStart, segmentEnd, edgeStart, edgeEnd)) {
            return true;
        }
    }
    return false;
};

/**
 * Checks if edges of two polygons intersect
 * @param {Array<google.maps.LatLng>} polygon1Points - First polygon vertices
 * @param {Array<google.maps.LatLng>} polygon2Points - Second polygon vertices
 * @returns {boolean} True if any edges intersect
 */
const doPolygonsEdgesIntersect = (polygon1Points, polygon2Points) => {
    // Check each edge of polygon1 against all edges of polygon2
    for (let i = 0; i < polygon1Points.length; i++) {
        const p1Start = polygon1Points[i];
        const p1End = polygon1Points[(i + 1) % polygon1Points.length];

        for (let j = 0; j < polygon2Points.length; j++) {
            const p2Start = polygon2Points[j];
            const p2End = polygon2Points[(j + 1) % polygon2Points.length];

            if (doLineSegmentsIntersect(p1Start, p1End, p2Start, p2End)) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Checks if a shape intersects with existing circles
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {Array<google.maps.Circle>} circles - Array of existing circles
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape intersects with any existing circle
 */
/**
 * Checks if a point is within a circle
 * @param {google.maps.LatLng} point - Point to check
 * @param {google.maps.Circle} circle - Circle to check against
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if point is inside circle
 */
const isPointInCircle = (point, circle, spherical) => {
    const distance = spherical.computeDistanceBetween(point, circle.getCenter());
    return distance <= circle.getRadius();
};

/**
 * Checks if a line segment intersects with a circle
 * @param {google.maps.LatLng} segmentStart - Start point of line segment
 * @param {google.maps.LatLng} segmentEnd - End point of line segment
 * @param {google.maps.Circle} circle - Circle to check against
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if segment intersects circle
 */
const doesLineSegmentIntersectCircle = (segmentStart, segmentEnd, circle, spherical) => {
    const circleCenter = circle.getCenter();
    const radius = circle.getRadius();

    // Check if either endpoint is inside the circle
    if (isPointInCircle(segmentStart, circle, spherical) ||
        isPointInCircle(segmentEnd, circle, spherical)) {
        return true;
    }

    // Calculate closest point on line segment to circle center
    const heading = spherical.computeHeading(segmentStart, segmentEnd);
    const distanceStartToEnd = spherical.computeDistanceBetween(segmentStart, segmentEnd);

    // Project circle center onto the line segment
    const headingToCenter = spherical.computeHeading(segmentStart, circleCenter);
    const angleDiff = headingToCenter - heading;
    const distanceStartToCenter = spherical.computeDistanceBetween(segmentStart, circleCenter);

    // Distance along the segment to the closest point
    const projectionDistance = distanceStartToCenter * Math.cos(angleDiff * Math.PI / 180);

    // Check if projection is within segment bounds
    if (projectionDistance < 0 || projectionDistance > distanceStartToEnd) {
        return false;
    }

    // Calculate perpendicular distance from center to line segment
    const perpendicularDistance = distanceStartToCenter * Math.abs(Math.sin(angleDiff * Math.PI / 180));

    return perpendicularDistance <= radius;
};

export const isShapeInsideExistingCircles = (newShape, shapeType, circles, polyLibrary, spherical) => {
    return circles.some((_circle) => {
        switch (shapeType) {
            case DRAWING_MODE.CIRCLE: {
                const centerDistance = spherical.computeDistanceBetween(
                    newShape.getCenter(),
                    _circle.getCenter()
                );
                // Circles intersect if distance between centers is less than sum of radii
                return centerDistance <= newShape.getRadius() + _circle.getRadius();
            }
            case DRAWING_MODE.POLYGON: {
                const existingPoints = parseCircleToPoints(_circle, spherical);
                const newPoints = newShape.getPath().getArray();

                // Check if any circle points are in polygon or polygon points are in circle
                return existingPoints.some((point) => polyLibrary.containsLocation(point, newShape))
                    || newPoints.some((point) => isPointInCircle(point, _circle, spherical));
            }
            case DRAWING_MODE.POLYLINE: {
                const points = newShape.getPath().getArray();

                // Check if any polyline segment intersects the circle
                for (let i = 0; i < points.length - 1; i++) {
                    if (doesLineSegmentIntersectCircle(points[i], points[i + 1], _circle, spherical)) {
                        return true;
                    }
                }

                return false;
            }
            case DRAWING_MODE.RECTANGLE: {
                const rectBounds = newShape.getBounds();
                const rectPoints = bboxToPoints(rectBounds);

                // Check if circle center is in rectangle
                if (rectBounds.contains(_circle.getCenter())) {
                    return true;
                }

                // Check if any rectangle vertex is in circle
                if (rectPoints.some(point => isPointInCircle(
                    new window.google.maps.LatLng(point.lat, point.lng),
                    _circle,
                    spherical
                ))) {
                    return true;
                }

                // Check if circle intersects any rectangle edge
                for (let i = 0; i < rectPoints.length; i++) {
                    const edgeStart = new window.google.maps.LatLng(rectPoints[i].lat, rectPoints[i].lng);
                    const edgeEnd = new window.google.maps.LatLng(
                        rectPoints[(i + 1) % rectPoints.length].lat,
                        rectPoints[(i + 1) % rectPoints.length].lng
                    );

                    if (doesLineSegmentIntersectCircle(edgeStart, edgeEnd, _circle, spherical)) {
                        return true;
                    }
                }

                return false;
            }
            default: return false;
        }
    });
};

/**
 * Checks if a shape intersects with existing rectangles
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {Array<google.maps.Rectangle>} rectangles - Array of existing rectangles
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape intersects with any existing rectangle
 */
export const isShapeInsideExistingRectangles = (newShape, shapeType, rectangles, polyLibrary, spherical) => {
    return rectangles.some((_rectangle) => {
        const existingBounds = _rectangle.getBounds();
        const existingPoints = bboxToPoints(existingBounds);

        switch (shapeType) {
            case DRAWING_MODE.CIRCLE: {
                const newPoints = parseCircleToPoints(newShape, spherical);
                const newShapeAsPolygon = new window.google.maps.Polygon({ paths: newPoints });
                const existingShapeAsPolygon = new window.google.maps.Polygon({ paths: existingPoints });
                return existingBounds.contains(newShape.getCenter())
                    || existingPoints.some((point) => polyLibrary.containsLocation(point, newShapeAsPolygon))
                    || newPoints.some((point) => polyLibrary.containsLocation(point, existingShapeAsPolygon));
            }
            case DRAWING_MODE.POLYGON: {
                const newPoints = newShape.getPath().getArray();
                const existingLatLngPoints = existingPoints.map(p => new window.google.maps.LatLng(p.lat, p.lng));
                const existingShapeAsPolygon = new window.google.maps.Polygon({ paths: existingPoints });

                // Check if any vertices are inside each other
                if (existingPoints.some((point) => polyLibrary.containsLocation(point, newShape)) ||
                    newPoints.some((point) => polyLibrary.containsLocation(point, existingShapeAsPolygon))) {
                    return true;
                }

                // Check if any edges intersect
                for (let i = 0; i < newPoints.length; i++) {
                    const newEdgeStart = newPoints[i];
                    const newEdgeEnd = newPoints[(i + 1) % newPoints.length];

                    for (let j = 0; j < existingLatLngPoints.length; j++) {
                        const existingEdgeStart = existingLatLngPoints[j];
                        const existingEdgeEnd = existingLatLngPoints[(j + 1) % existingLatLngPoints.length];

                        if (doLineSegmentsIntersect(newEdgeStart, newEdgeEnd, existingEdgeStart, existingEdgeEnd)) {
                            return true;
                        }
                    }
                }

                return false;
            }
            case DRAWING_MODE.POLYLINE: {
                const points = newShape.getPath().getArray();

                // Check if any line segment intersects the rectangle edges
                for (let i = 0; i < points.length - 1; i++) {
                    const segmentStart = points[i];
                    const segmentEnd = points[i + 1];

                    // Check if segment intersects rectangle or if endpoints are inside
                    if (doesLineSegmentIntersectRectangle(segmentStart, segmentEnd, existingBounds)) {
                        return true;
                    }
                }

                return false;
            }
            case DRAWING_MODE.RECTANGLE: {
                const newBounds = newShape.getBounds();
                const newPoints = bboxToPoints(newBounds);

                // Check if any vertices are inside each other
                const existingLatLngPoints = existingPoints.map(p => new window.google.maps.LatLng(p.lat, p.lng));
                const newLatLngPoints = newPoints.map(p => new window.google.maps.LatLng(p.lat, p.lng));

                if (existingLatLngPoints.some(point => newBounds.contains(point)) ||
                    newLatLngPoints.some(point => existingBounds.contains(point))) {
                    return true;
                }

                // Check if any edges intersect
                for (let i = 0; i < newPoints.length; i++) {
                    const newEdgeStart = newLatLngPoints[i];
                    const newEdgeEnd = newLatLngPoints[(i + 1) % newLatLngPoints.length];

                    for (let j = 0; j < existingPoints.length; j++) {
                        const existingEdgeStart = existingLatLngPoints[j];
                        const existingEdgeEnd = existingLatLngPoints[(j + 1) % existingLatLngPoints.length];

                        if (doLineSegmentsIntersect(newEdgeStart, newEdgeEnd, existingEdgeStart, existingEdgeEnd)) {
                            return true;
                        }
                    }
                }

                return false;
            }
            default: return false;
        }
    });
};

/**
 * Checks if a shape intersects with a polygon
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {google.maps.Polygon} polygon - The polygon to check against
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape intersects with the polygon
 */
export const isShapeInsidePolygon = (newShape, shapeType, polygon, polyLibrary, spherical) => {
    if (!polyLibrary) return false;

    // Normalize polygon path
    const originalPath = polygon.getPath().getArray();
    const first = originalPath[0];
    const last = originalPath[originalPath.length - 1];
    const hasDuplicateClosingVertex = originalPath.length > 2 &&
        first.lat() === last.lat() &&
        first.lng() === last.lng();

    const normalizedPolygon = hasDuplicateClosingVertex
        ? new window.google.maps.Polygon({ paths: originalPath.slice(0, -1) })
        : polygon;

    const existingPoints = normalizedPolygon.getPath().getArray();
    const containsInPolygon = (point) => polyLibrary.containsLocation(point, normalizedPolygon);

    switch (shapeType) {
        case DRAWING_MODE.CIRCLE: {
            const circlePoints = parseCircleToPoints(newShape, spherical);
            const newShapeAsPolygon = new window.google.maps.Polygon({ paths: circlePoints });
            return containsInPolygon(newShape.getCenter())
                || circlePoints.some(containsInPolygon)
                || existingPoints.some((point) => polyLibrary.containsLocation(point, newShapeAsPolygon));
        }
        case DRAWING_MODE.POLYGON: {
            const newPoints = newShape.getPath().getArray();

            // Check vertex containment
            if (existingPoints.some(vertex => polyLibrary.containsLocation(vertex, newShape)) ||
                newPoints.some(containsInPolygon)) {
                return true;
            }

            // Check edge intersections
            return doPolygonsEdgesIntersect(newPoints, existingPoints);
        }
        case DRAWING_MODE.POLYLINE: {
            const polylinePoints = newShape.getPath().getArray();

            // Check each polyline segment
            for (let i = 0; i < polylinePoints.length - 1; i++) {
                const segmentStart = polylinePoints[i];
                const segmentEnd = polylinePoints[i + 1];

                // Check if segment intersects polygon or endpoints are inside
                if (doesLineSegmentIntersectPolygon(segmentStart, segmentEnd, existingPoints) ||
                    containsInPolygon(segmentStart) ||
                    containsInPolygon(segmentEnd)) {
                    return true;
                }
            }

            return false;
        }
        case DRAWING_MODE.RECTANGLE: {
            const newPoints = bboxToPoints(newShape.getBounds());
            const newShapeAsPolygon = new window.google.maps.Polygon({ paths: newPoints });

            return existingPoints.some((point) => polyLibrary.containsLocation(point, newShapeAsPolygon)) ||
                newPoints.some(containsInPolygon);
        }
        default: return false;
    }
};

/**
 * Checks if a shape is inside the country boundary
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {google.maps.Polygon} countryShape - The country boundary polygon
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape is inside the country
 */
export const isInsideTheCountry = (newShape, shapeType, countryShape, polyLibrary, spherical) => {
    if (countryShape) {
        return isShapeInsidePolygon(newShape, shapeType, countryShape, polyLibrary, spherical);
    }
    return false;
};

/**
 * Returns the bounding box of a shape for quick spatial pre-checks.
 * @param {google.maps.MVCObject} newShape - The shape to get bounds for
 * @param {string} shapeType - Shape type from DRAWING_MODE constants
 * @returns {google.maps.LatLngBounds|null} The shape's bounding box, or null if unavailable
 */
const getShapeBounds = (newShape, shapeType) => {
    switch (shapeType) {
        case DRAWING_MODE.CIRCLE:
        case DRAWING_MODE.RECTANGLE:
            return newShape.getBounds?.() ?? null;
        case DRAWING_MODE.POLYGON:
        case DRAWING_MODE.POLYLINE: {
            const bounds = new window.google.maps.LatLngBounds();
            newShape.getPath().getArray().forEach(p => bounds.extend(p));
            return bounds;
        }
        default:
            return null;
    }
};

/**
 * Checks if a shape intersects with locality boundary
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {{ polygon: google.maps.Polygon, combinedBounds: google.maps.LatLngBounds, pathBounds: google.maps.LatLngBounds[] }} localityBoundary - The locality boundary with pre-computed bounds
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape intersects with locality boundary
 */
export const isShapeInsideLocalityBoundary = (newShape, shapeType, localityBoundary, polyLibrary, spherical) => {
    if (!localityBoundary?.polygon) return false;

    const { polygon: localityPolygon, combinedBounds, pathBounds } = localityBoundary;
    const shapeBounds = getShapeBounds(newShape, shapeType);

    // Coarse check: if the shape is entirely outside all locality bounds, skip all path checks
    if (shapeBounds && combinedBounds && !shapeBounds.intersects(combinedBounds)) {
        return false;
    }

    return localityPolygon.getPaths().getArray().some((_path, index) => {
        // Per-path bounds check: skip paths whose pre-computed bounds don't overlap
        if (shapeBounds && pathBounds?.[index] && !shapeBounds.intersects(pathBounds[index])) {
            return false;
        }
        return isShapeInsidePolygon(newShape, shapeType, new window.google.maps.Polygon({ paths: _path }), polyLibrary, spherical);
    });
};

/**
 * Checks if a shape overlaps with existing polygons
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {Array<google.maps.Polygon>} polygons - Array of existing polygons
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape overlaps with any existing polygon
 */
export const isShapeInsideExistingPolygons = (newShape, shapeType, polygons, polyLibrary, spherical) => {
    return polygons.some((_polygon) => isShapeInsidePolygon(newShape, shapeType, _polygon, polyLibrary, spherical));
};

/**
 * Checks if a shape intersects with existing polylines
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {Array<google.maps.Polyline>} polylines - Array of existing polylines
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @returns {boolean} True if shape intersects with any existing polyline
 */
export const isShapeInsideExistingPolylines = (newShape, shapeType, polylines, polyLibrary, spherical) => {
    return polylines.some((_polyline) => {
        const polylinePoints = _polyline.getPath().getArray();

        switch (shapeType) {
            case DRAWING_MODE.CIRCLE: {
                // Check if any polyline segment intersects the circle boundary
                for (let i = 0; i < polylinePoints.length - 1; i++) {
                    if (doesLineSegmentIntersectCircle(polylinePoints[i], polylinePoints[i + 1], newShape, spherical)) {
                        return true;
                    }
                }
                // Check if ANY point is inside the circle (partial or complete containment)
                return polylinePoints.some(point => isPointInCircle(point, newShape, spherical));
            }
            case DRAWING_MODE.POLYGON: {
                const newPoints = newShape.getPath().getArray();

                // Check if any polyline segment intersects polygon edges
                for (let i = 0; i < polylinePoints.length - 1; i++) {
                    const segmentStart = polylinePoints[i];
                    const segmentEnd = polylinePoints[i + 1];

                    if (doesLineSegmentIntersectPolygon(segmentStart, segmentEnd, newPoints)) {
                        return true;
                    }
                }

                // Check if any polyline point is inside polygon
                return polylinePoints.some(point => polyLibrary.containsLocation(point, newShape));
            }
            case DRAWING_MODE.POLYLINE: {
                const newPoints = newShape.getPath().getArray();

                // Check if any segment from new polyline intersects existing polyline segments
                for (let i = 0; i < newPoints.length - 1; i++) {
                    const newSegmentStart = newPoints[i];
                    const newSegmentEnd = newPoints[i + 1];

                    for (let j = 0; j < polylinePoints.length - 1; j++) {
                        const existingSegmentStart = polylinePoints[j];
                        const existingSegmentEnd = polylinePoints[j + 1];

                        if (doLineSegmentsIntersect(newSegmentStart, newSegmentEnd, existingSegmentStart, existingSegmentEnd)) {
                            return true;
                        }
                    }
                }
                return false;
            }
            case DRAWING_MODE.RECTANGLE: {
                const rectBounds = newShape.getBounds();

                // Check if any polyline segment intersects rectangle edges
                for (let i = 0; i < polylinePoints.length - 1; i++) {
                    if (doesLineSegmentIntersectRectangle(polylinePoints[i], polylinePoints[i + 1], rectBounds)) {
                        return true;
                    }
                }

                // Check if any polyline point is inside rectangle
                return polylinePoints.some(point => rectBounds.contains(point));
            }
            default: return false;
        }
    });
};

/**
 * Checks if a shape overlaps with any existing shapes
 * @param {google.maps.MVCObject} newShape - The new shape to check
 * @param {string} shapeType - Type of the new shape (from DRAWING_MODE constants)
 * @param {Object} existingShapes - Object containing arrays of existing shapes
 * @param {Array<google.maps.Circle>} existingShapes.circles - Array of existing circles
 * @param {Array<google.maps.Polygon>} existingShapes.polygons - Array of existing polygons
 * @param {Array<google.maps.Rectangle>} existingShapes.rectangles - Array of existing rectangles
 * @param {google.maps.Polygon} localityBoundary - The locality boundary polygon
 * @param {google.maps.geometry.spherical} spherical - Google Maps spherical geometry library
 * @param {google.maps.geometry.poly} polyLibrary - Google Maps poly geometry library
 * @returns {boolean} True if shape overlaps with any existing shape
 */
export const isShapeInsideExistingShapes = (
    newShape,
    shapeType,
    existingShapes,
    localityBoundary,
    polyLibrary,
    spherical
) => {
    if (!spherical || !polyLibrary) return false;
    return (
        isShapeInsideExistingCircles(newShape, shapeType, existingShapes.circles, polyLibrary, spherical) ||
        isShapeInsideExistingPolygons(newShape, shapeType, existingShapes.polygons, polyLibrary, spherical) ||
        isShapeInsideExistingRectangles(newShape, shapeType, existingShapes.rectangles, polyLibrary, spherical) ||
        isShapeInsideExistingPolylines(newShape, shapeType, existingShapes.polylines || [], polyLibrary, spherical) ||
        isShapeInsideLocalityBoundary(newShape, shapeType, localityBoundary, polyLibrary, spherical)
    );
};

export default {
    isInsideTheCountry,
    isShapeInsideExistingShapes,
}

